/**
 * interact/system.ts — The Interaction System
 *
 * This is the bridge between a mouse click and a piece of furniture coming
 * alive. Every frame, the game loop calls update() with the current input.
 * When a player clicks furniture, this system decides what to do:
 *
 *   - If the player is close enough → execute the action immediately.
 *   - If not → register a pending interaction and let the character walk there.
 *     When they arrive, the loop asks hasPendingInteraction() and fires it.
 *
 * The system also provides tooltip info for whatever furniture the player is
 * currently hovering over — name, description, a gentle hint.
 *
 * "A room full of furniture that responds to you — that is a home."
 * — room/furniture.ts
 */

import type { GameState, FurniturePlacement } from "../engine/state.js";
import type { InputState } from "../engine/input.js";
import { getFurnitureType } from "../room/furniture.js";
import {
  getActionsForFurniture,
  getHintForInteractionType,
  type InteractionResult,
} from "./actions.js";

// ─── Tooltip Info ─────────────────────────────────────────────────────────────

/**
 * Information the renderer needs to draw a furniture tooltip.
 * The hint is derived from interactionType — "Click to sit", etc.
 */
export interface TooltipInfo {
  name: string;
  description: string;
  hint: string;
  /** Screen X for tooltip anchor (isometric tile center) */
  screenX: number;
  /** Screen Y for tooltip anchor (isometric tile top) */
  screenY: number;
}

// ─── Registry ID Mapping ─────────────────────────────────────────────────────

/**
 * The starter room uses short type strings ("bed", "desk", "aeterna") that
 * aren't registry IDs. This map translates them to the canonical IDs in the
 * furniture registry so we can look up FurnitureType.
 *
 * Any furniture placement whose type already matches a registry ID passes
 * through unchanged; this map only covers the short aliases used by
 * createStarterRoom() and any hand-authored room data.
 */
const TYPE_ALIAS_MAP: Record<string, string> = {
  bed:          "bed_cozy",
  desk:         "desk_study",
  aeterna:      "aeterna_device",
  desk_chair:   "chair_wooden",
  rug_round:    "rug_woven",
  floor_lamp:   "floor_lamp",       // matches registry directly, listed for clarity
  potted_fern:  "potted_plant",
};

/**
 * Resolve a placement's type string to a furniture registry ID.
 * First checks the alias map, then falls back to the raw type (in case
 * it was already a canonical ID like "sofa_oak" or "piano_upright").
 */
function resolveRegistryId(placementType: string): string {
  return TYPE_ALIAS_MAP[placementType] ?? placementType;
}

// ─── Adjacency ────────────────────────────────────────────────────────────────

/**
 * Manhattan distance between two tile positions.
 * We use ≤ 2 as "adjacent enough to interact" — lets the player reach
 * furniture on the next tile without needing to be perfectly beside it.
 */
function manhattanDistance(
  ax: number, ay: number,
  bx: number, by: number
): number {
  return Math.abs(ax - bx) + Math.abs(ay - by);
}

/**
 * Direction deltas: which tile offset is "in front" of each compass direction.
 * If a chair faces "W", the tile in front of it is to the west (dx:-1, dy:0).
 */
const DIR_FRONT: Record<string, { dx: number; dy: number }> = {
  E:  { dx:  1, dy:  0 },
  SE: { dx:  1, dy:  1 },
  S:  { dx:  0, dy:  1 },
  SW: { dx: -1, dy:  1 },
  W:  { dx: -1, dy:  0 },
  NW: { dx: -1, dy: -1 },
  N:  { dx:  0, dy: -1 },
  NE: { dx:  1, dy: -1 },
};

/**
 * Find the best adjacent tile for the player to walk to before interacting.
 *
 * Prioritises the tile in FRONT of the furniture (based on its direction),
 * then tries adjacent tiles, then diagonals. Falls back to the furniture
 * tile itself if nothing is walkable.
 */
function findAdjacentTile(
  furniture: FurniturePlacement,
  state: GameState
): { tileX: number; tileY: number } {
  const room = state.currentRoom!;
  const cx = furniture.tileX;
  const cy = furniture.tileY;

  // The "front" of the furniture — where the player should stand to interact
  const front = DIR_FRONT[furniture.direction.toUpperCase()] ?? { dx: 0, dy: 1 };

  // Build candidate list: front tile first, then the other 3 cardinals, then diagonals
  const allCardinals = [
    { dx:  0, dy:  1 },
    { dx:  0, dy: -1 },
    { dx:  1, dy:  0 },
    { dx: -1, dy:  0 },
  ];
  const allDiagonals = [
    { dx:  1, dy:  1 },
    { dx: -1, dy:  1 },
    { dx:  1, dy: -1 },
    { dx: -1, dy: -1 },
  ];

  // Front tile is highest priority
  const candidates: { dx: number; dy: number }[] = [front];
  // Then remaining cardinals (skip front if it's already a cardinal)
  for (const c of allCardinals) {
    if (c.dx !== front.dx || c.dy !== front.dy) candidates.push(c);
  }
  // Then diagonals (skip front if it was a diagonal)
  for (const c of allDiagonals) {
    if (c.dx !== front.dx || c.dy !== front.dy) candidates.push(c);
  }

  for (const { dx, dy } of candidates) {
    const tx = cx + dx;
    const ty = cy + dy;
    if (tx < 0 || ty < 0 || tx >= room.width || ty >= room.height) continue;
    const tile = room.tiles[ty]?.[tx];
    if (tile?.walkable) {
      return { tileX: tx, tileY: ty };
    }
  }

  // No walkable neighbour found — return the furniture tile as fallback
  return { tileX: cx, tileY: cy };
}

// ─── Notification Helper ──────────────────────────────────────────────────────

/**
 * Push a gentle notification into the UI notification queue.
 * Notifications expire after 4 seconds by default.
 */
function pushNotification(state: GameState, text: string, durationMs = 4000): void {
  const id = `notif_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
  state.ui.notifications.push({
    id,
    text,
    expiresAt: Date.now() + durationMs,
  });
}

// ─── Interaction System ───────────────────────────────────────────────────────

/**
 * The InteractionSystem.
 *
 * Create once, hand it to the game loop. Call update() every frame.
 * When the player finishes walking toward furniture, call update() again
 * and it will execute the pending interaction.
 */
export class InteractionSystem {
  /**
   * An interaction we're waiting to execute once the player arrives.
   * The furniture ID and the target tile the player should reach.
   */
  private pendingInteraction: {
    furnitureId: string;
    targetTile: { tileX: number; tileY: number };
  } | null = null;

  /** Timestamp of the last successful interaction — prevents rapid spam. */
  private lastInteractionTime = 0;

  /** Minimum milliseconds between interactions */
  private readonly COOLDOWN_MS = 500;

  // ─── Public API ────────────────────────────────────────────────────────────

  /**
   * Called every frame by the game loop.
   *
   * Checks for:
   *   1. A freshly clicked furniture tile → walk there or interact immediately.
   *   2. A pending interaction whose target tile the player has now reached.
   *
   * Returns an InteractionResult if something just happened (so the loop
   * can trigger sounds), or null if this frame was quiet.
   */
  update(state: GameState, inputState: InputState): InteractionResult | null {
    if (!state.currentRoom) return null;

    const now = Date.now();

    // ── 1. New click on furniture ──────────────────────────────────────────
    // Only process furniture clicks in normal mode (not edit/build mode).
    // In edit mode, clicks select furniture for move/rotate/pickup instead.
    if (inputState.clickedTile && !state.ui.editMode && !state.ui.uiClickConsumed) {
      const { tx, ty } = inputState.clickedTile;
      const room = state.currentRoom;

      if (tx >= 0 && tx < room.width && ty >= 0 && ty < room.height) {
        const tile = room.tiles[ty]?.[tx];

        if (tile?.furniture) {
          const placement = this.findPlacement(state, tile.furniture);
          if (placement) {
            const result = this.handleFurnitureClick(state, placement, now);
            return result;
          }
        }
      }
    }

    // ── 2. Check pending interaction ──────────────────────────────────────
    if (this.pendingInteraction && now - this.lastInteractionTime >= this.COOLDOWN_MS) {
      const { furnitureId, targetTile } = this.pendingInteraction;
      const player = state.player;

      // Has the player arrived at (or past) the target tile?
      const arrived =
        player.position.tileX === targetTile.tileX &&
        player.position.tileY === targetTile.tileY &&
        player.animState !== "walk";

      if (arrived) {
        const placement = this.findPlacement(state, furnitureId);
        this.pendingInteraction = null;

        if (placement) {
          return this.executeInteraction(state, placement, now);
        }
      }
    }

    return null;
  }

  /**
   * Returns tooltip information for the furniture tile currently under the
   * player's cursor, or null if the hovered tile has no interactable furniture.
   *
   * The renderer calls this each frame to decide what tooltip to draw.
   */
  getHoveredFurnitureInfo(state: GameState): TooltipInfo | null {
    if (!state.currentRoom || !state.ui.hoveredTile) return null;

    const { x, y } = state.ui.hoveredTile;
    const room = state.currentRoom;

    if (x < 0 || y < 0 || x >= room.width || y >= room.height) return null;

    const tile = room.tiles[y]?.[x];
    if (!tile?.furniture) return null;

    const placement = this.findPlacement(state, tile.furniture);
    if (!placement) return null;

    const registryId = resolveRegistryId(placement.type);
    const furnitureType = getFurnitureType(registryId);
    if (!furnitureType) return null;

    // Only show tooltip for interactable furniture
    if (!furnitureType.interactable && !furnitureType.interactionType) return null;

    const hint = furnitureType.interactionType
      ? getHintForInteractionType(furnitureType.interactionType)
      : "Look";

    // Project the tile to screen coordinates for tooltip placement.
    // We use the same isometric formula the renderer uses: 2:1 diamond.
    const cam = state.camera;
    const wx = x + 0.5;  // tile center
    const wy = y + 0.5;
    const screenX = cam.x + (wx - wy) * 32 * cam.zoom;
    const screenY = cam.y + (wx + wy) * 16 * cam.zoom - 40 * cam.zoom; // above tile top

    return {
      name: furnitureType.name,
      description: furnitureType.description,
      hint,
      screenX,
      screenY,
    };
  }

  /** True if there is an interaction queued and waiting for the player to arrive. */
  hasPendingInteraction(): boolean {
    return this.pendingInteraction !== null;
  }

  /** Cancel any queued interaction (e.g. if the player clicked somewhere else). */
  clearPendingInteraction(): void {
    this.pendingInteraction = null;
  }

  /**
   * Directly trigger interaction with a specific furniture by ID.
   * Used by interact-prompt hit regions (mouse click on tooltip).
   * Behaves like handleFurnitureClick but bypasses the clickedTile path.
   */
  interactWithFurniture(state: GameState, furnitureId: string): InteractionResult | null {
    const placement = this.findPlacement(state, furnitureId);
    if (!placement) return null;
    return this.handleFurnitureClick(state, placement, Date.now());
  }

  /**
   * Called by the game loop when the player finishes walking.
   * Checks whether they arrived at the queued interaction target and fires it.
   */
  checkPendingArrival(state: GameState): InteractionResult | null {
    if (!this.pendingInteraction) return null;

    const { furnitureId, targetTile } = this.pendingInteraction;
    const player = state.player;

    // Are we at the target tile?
    if (player.position.tileX !== targetTile.tileX || player.position.tileY !== targetTile.tileY) {
      return null; // not there yet
    }

    // Find the furniture and execute
    const placement = this.findPlacement(state, furnitureId);
    this.pendingInteraction = null;

    if (!placement) return null;
    return this.executeInteraction(state, placement, Date.now());
  }

  // ─── Internal ─────────────────────────────────────────────────────────────

  /**
   * Handle a click on a furniture placement.
   *
   * If the player is within Manhattan distance 2, execute immediately.
   * Otherwise, set up a pendingInteraction and tell the game loop to walk there.
   */
  private handleFurnitureClick(
    state: GameState,
    placement: FurniturePlacement,
    now: number
  ): InteractionResult | null {
    // Respect cooldown — prevents double-fire on rapid clicks
    if (now - this.lastInteractionTime < this.COOLDOWN_MS) return null;

    const player = state.player;
    const dist = manhattanDistance(
      player.position.tileX,
      player.position.tileY,
      placement.tileX,
      placement.tileY
    );

    if (dist <= 2) {
      // Player is close enough — interact right now
      return this.executeInteraction(state, placement, now);
    } else {
      // Player needs to walk closer — queue it up
      const targetTile = findAdjacentTile(placement, state);

      this.pendingInteraction = {
        furnitureId: placement.id,
        targetTile,
      };

      // Kick off walking: set the movement queue so the character loop handles it
      state.player.targetPosition = targetTile;
      state.player.movementQueue = [targetTile];
      state.player.animState = "walk";

      // Compute walking direction
      const dx = targetTile.tileX - player.position.tileX;
      const dy = targetTile.tileY - player.position.tileY;
      state.player.direction = directionFromDelta(dx, dy);

      return null; // result will come later when they arrive
    }
  }

  /**
   * Execute the primary interaction for a furniture placement.
   *
   * Looks up the FurnitureType, finds the actions, runs the first one,
   * applies the result (notification, animation, facing direction), then
   * returns the result so the caller can trigger audio.
   */
  private executeInteraction(
    state: GameState,
    placement: FurniturePlacement,
    now: number
  ): InteractionResult | null {
    const registryId = resolveRegistryId(placement.type);
    const furnitureType = getFurnitureType(registryId);

    if (!furnitureType) {
      // Unknown furniture — graceful no-op
      return null;
    }

    const interactionType = furnitureType.interactionType;
    if (!interactionType) {
      // Not interactable (rug, non-interactive table, etc.)
      return null;
    }

    const actions = getActionsForFurniture(interactionType);
    if (actions.length === 0) {
      // We know the type but have no handler — quiet failure
      return null;
    }

    // Execute the primary (first) action
    const primaryAction = actions[0];
    const result = primaryAction.execute(state, placement);

    // Update cooldown
    this.lastInteractionTime = now;

    // Apply notification
    if (result.message) {
      pushNotification(state, result.message);
    }

    // Apply player animation
    if (result.playerAnim) {
      // The execute() function sets animState directly on state.player,
      // but we honour the returned value here too in case a caller needs it.
      state.player.animState = result.playerAnim as import("../engine/state.js").AnimState;
    }

    // Apply player direction
    if (result.playerDir) {
      state.player.direction = result.playerDir as import("../engine/state.js").Direction;
    }

    return result;
  }

  /**
   * Find a FurniturePlacement by ID within the current room.
   */
  private findPlacement(
    state: GameState,
    furnitureId: string
  ): FurniturePlacement | undefined {
    return state.currentRoom?.furniture.find((f) => f.id === furnitureId);
  }
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * 8-directional facing from a delta vector.
 * Duplicated here so system.ts has no dependency on actions.ts internals.
 */
function directionFromDelta(dx: number, dy: number): import("../engine/state.js").Direction {
  if (dx > 0 && dy === 0) return "SE";
  if (dx < 0 && dy === 0) return "NW";
  if (dx === 0 && dy > 0) return "SW";
  if (dx === 0 && dy < 0) return "NE";
  if (dx > 0 && dy > 0) return "S";
  if (dx < 0 && dy < 0) return "N";
  if (dx > 0 && dy < 0) return "E";
  if (dx < 0 && dy > 0) return "W";
  return "S";
}
